/**
 * Admin Localization Management Page
 * Manage countries and currencies - enable/disable, reorder, add new
 * Also manage country-specific content (hero text, tagline)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Globe, DollarSign, Plus, Trash2, ArrowUp, ArrowDown, 
  Save, RefreshCw, Star, Loader2, AlertCircle, Check, Edit, FileText, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLocalization = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [defaultCountry, setDefaultCountry] = useState('SL');
  const [defaultCurrency, setDefaultCurrency] = useState('LKR');
  const [showAddCountry, setShowAddCountry] = useState(false);
  const [showAddCurrency, setShowAddCurrency] = useState(false);
  const [showEditContent, setShowEditContent] = useState(false);
  const [showEditLocations, setShowEditLocations] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [editingContent, setEditingContent] = useState({
    hero_title: '', hero_subtitle: '', tagline: '', footer_description: '', enabled: true
  });
  const [editingLocations, setEditingLocations] = useState({
    enabled: true, title: 'Top Locations', locations: [], locationsText: ''
  });
  const [showEditFooter, setShowEditFooter] = useState(false);
  const [editingFooter, setEditingFooter] = useState({
    enabled: true,
    contact: { enabled: true, title: 'Contact Us', address: '', phone: '', email: '' },
    quick_links: { enabled: true, title: 'Quick Links', links: [] },
    categories: { enabled: true, title: 'Categories', links: [] },
    quickLinksText: '',
    categoriesText: ''
  });
  const [newCountry, setNewCountry] = useState({
    code: '', name: '', flag: '', currency: '', currency_symbol: '', distance_unit: 'km'
  });
  const [newCurrency, setNewCurrency] = useState({
    code: '', symbol: '', name: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [countriesRes, currenciesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/localization/countries`),
        axios.get(`${API_URL}/api/admin/localization/currencies`)
      ]);
      
      setCountries(countriesRes.data.countries || []);
      setCurrencies(currenciesRes.data.currencies || []);
      setDefaultCountry(countriesRes.data.default_country || 'SL');
      setDefaultCurrency(currenciesRes.data.default_currency || 'LKR');
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load localization settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleCountry = async (code, currentEnabled) => {
    try {
      await axios.put(`${API_URL}/api/admin/localization/countries/${code}`, {
        enabled: !currentEnabled
      });
      
      setCountries(prev => prev.map(c => 
        c.code === code ? { ...c, enabled: !currentEnabled } : c
      ));
      
      toast.success(`Country ${code} ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update country');
    }
  };

  const toggleCurrency = async (code, currentEnabled) => {
    try {
      await axios.put(`${API_URL}/api/admin/localization/currencies/${code}`, {
        enabled: !currentEnabled
      });
      
      setCurrencies(prev => prev.map(c => 
        c.code === code ? { ...c, enabled: !currentEnabled } : c
      ));
      
      toast.success(`Currency ${code} ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update currency');
    }
  };

  const setAsDefaultCountry = async (code) => {
    try {
      await axios.put(`${API_URL}/api/admin/localization/defaults`, null, {
        params: { default_country: code }
      });
      setDefaultCountry(code);
      toast.success(`${code} set as default country`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set default');
    }
  };

  const setAsDefaultCurrency = async (code) => {
    try {
      await axios.put(`${API_URL}/api/admin/localization/defaults`, null, {
        params: { default_currency: code }
      });
      setDefaultCurrency(code);
      toast.success(`${code} set as default currency`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set default');
    }
  };

  const deleteCountry = async (code) => {
    if (!window.confirm(`Are you sure you want to delete ${code}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/localization/countries/${code}`);
      setCountries(prev => prev.filter(c => c.code !== code));
      toast.success(`Country ${code} deleted`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete country');
    }
  };

  const openEditContent = (country) => {
    setEditingCountry(country);
    setEditingContent({
      hero_title: country.content?.hero_title || `Buy & Sell Anything in ${country.name}`,
      hero_subtitle: country.content?.hero_subtitle || 'Join thousands of people buying and selling products, services, and more',
      tagline: country.content?.tagline || `${country.name}'s most trusted marketplace`,
      footer_description: country.content?.footer_description || 'Buy. Sell. Connect with confidence.\n\nZeilian™️ combines Trust Intelligence™️ and Market Intelligence™️ to help you make better decisions before you act.',
      enabled: country.content?.enabled !== false
    });
    setShowEditContent(true);
  };

  const saveContent = async () => {
    if (!editingCountry) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/localization/countries/${editingCountry.code}/content`,
        editingContent
      );
      
      // Update local state
      setCountries(prev => prev.map(c => 
        c.code === editingCountry.code 
          ? { ...c, content: editingContent }
          : c
      ));
      
      setShowEditContent(false);
      toast.success(`Content updated for ${editingCountry.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update content');
    } finally {
      setSaving(false);
    }
  };

  const openEditLocations = (country) => {
    setEditingCountry(country);
    const locations = country.top_locations?.locations || [];
    setEditingLocations({
      enabled: country.top_locations?.enabled !== false,
      title: country.top_locations?.title || 'Top Locations',
      locations: locations,
      locationsText: locations.join(', ')
    });
    setShowEditLocations(true);
  };

  const saveLocations = async () => {
    if (!editingCountry) return;
    
    // Parse locations from text
    const locationsArray = editingLocations.locationsText
      .split(',')
      .map(l => l.trim())
      .filter(l => l.length > 0);
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/localization/countries/${editingCountry.code}/top-locations`,
        {
          enabled: editingLocations.enabled,
          title: editingLocations.title,
          locations: locationsArray
        }
      );
      
      // Update local state
      setCountries(prev => prev.map(c => 
        c.code === editingCountry.code 
          ? { ...c, top_locations: { ...editingLocations, locations: locationsArray } }
          : c
      ));
      
      setShowEditLocations(false);
      toast.success(`Top locations updated for ${editingCountry.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update locations');
    } finally {
      setSaving(false);
    }
  };

  // Helper to parse links text into array
  const parseLinksText = (text) => {
    return text.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        const [label, url] = line.split('|').map(s => s.trim());
        return { label: label || '', url: url || '/' };
      });
  };

  // Helper to format links array to text
  const formatLinksToText = (links) => {
    return (links || []).map(l => `${l.label}|${l.url}`).join('\n');
  };

  const openEditFooter = (country) => {
    setEditingCountry(country);
    const footer = country.footer || {};
    setEditingFooter({
      enabled: footer.enabled !== false,
      contact: {
        enabled: footer.contact?.enabled !== false,
        title: footer.contact?.title || 'Contact Us',
        address: footer.contact?.address || '',
        phone: footer.contact?.phone || '',
        phone_enabled: footer.contact?.phone_enabled !== false,
        email: footer.contact?.email || ''
      },
      quick_links: {
        enabled: footer.quick_links?.enabled !== false,
        title: footer.quick_links?.title || 'Quick Links',
        links: footer.quick_links?.links || []
      },
      categories: {
        enabled: footer.categories?.enabled !== false,
        title: footer.categories?.title || 'Categories',
        links: footer.categories?.links || []
      },
      disclaimer: {
        enabled: footer.disclaimer?.enabled !== false,
        text: footer.disclaimer?.text || 'While every care has been taken compiling this product, the publishers cannot accept responsibility for any inaccuracies or errors herein.'
      },
      quickLinksText: formatLinksToText(footer.quick_links?.links),
      categoriesText: formatLinksToText(footer.categories?.links)
    });
    setShowEditFooter(true);
  };

  const saveFooter = async () => {
    if (!editingCountry) return;
    
    // Parse links from text
    const quickLinksArray = parseLinksText(editingFooter.quickLinksText);
    const categoriesArray = parseLinksText(editingFooter.categoriesText);
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/localization/countries/${editingCountry.code}/footer`,
        {
          enabled: editingFooter.enabled,
          contact: editingFooter.contact,
          quick_links: {
            enabled: editingFooter.quick_links.enabled,
            title: editingFooter.quick_links.title,
            links: quickLinksArray
          },
          categories: {
            enabled: editingFooter.categories.enabled,
            title: editingFooter.categories.title,
            links: categoriesArray
          },
          disclaimer: editingFooter.disclaimer
        }
      );
      
      // Update local state
      setCountries(prev => prev.map(c => 
        c.code === editingCountry.code 
          ? { 
              ...c, 
              footer: { 
                enabled: editingFooter.enabled,
                contact: editingFooter.contact,
                quick_links: { ...editingFooter.quick_links, links: quickLinksArray },
                categories: { ...editingFooter.categories, links: categoriesArray },
                disclaimer: editingFooter.disclaimer
              } 
            }
          : c
      ));
      
      setShowEditFooter(false);
      toast.success(`Footer updated for ${editingCountry.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update footer');
    } finally {
      setSaving(false);
    }
  };

  const deleteCurrency = async (code) => {
    if (!window.confirm(`Are you sure you want to delete ${code}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/localization/currencies/${code}`);
      setCurrencies(prev => prev.filter(c => c.code !== code));
      toast.success(`Currency ${code} deleted`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete currency');
    }
  };

  const addCountry = async () => {
    if (!newCountry.code || !newCountry.name) {
      toast.error('Code and name are required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/localization/countries`, newCountry);
      setCountries(prev => [...prev, response.data.country]);
      setShowAddCountry(false);
      setNewCountry({ code: '', name: '', flag: '', currency: '', currency_symbol: '', distance_unit: 'km' });
      toast.success('Country added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add country');
    } finally {
      setSaving(false);
    }
  };

  const addCurrency = async () => {
    if (!newCurrency.code || !newCurrency.symbol || !newCurrency.name) {
      toast.error('All fields are required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/localization/currencies`, newCurrency);
      setCurrencies(prev => [...prev, response.data.currency]);
      setShowAddCurrency(false);
      setNewCurrency({ code: '', symbol: '', name: '' });
      toast.success('Currency added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add currency');
    } finally {
      setSaving(false);
    }
  };

  const moveCountry = async (code, direction) => {
    const index = countries.findIndex(c => c.code === code);
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === countries.length - 1) return;
    
    const newOrder = direction === 'up' ? countries[index - 1].order : countries[index + 1].order;
    
    try {
      await axios.put(`${API_URL}/api/admin/localization/countries/${code}`, { order: newOrder });
      fetchData(); // Refresh to get correct order
    } catch (err) {
      toast.error('Failed to reorder');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const enabledCountries = countries.filter(c => c.enabled).length;
  const enabledCurrencies = currencies.filter(c => c.enabled).length;

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="admin-localization">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Globe className="h-6 w-6 text-blue-600" />
              Localization Management
            </h1>
            <p className="text-gray-500 mt-1">Manage countries and currencies for your marketplace</p>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Globe className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Countries</p>
                  <p className="text-xl font-bold">{countries.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Enabled Countries</p>
                  <p className="text-xl font-bold">{enabledCountries}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Currencies</p>
                  <p className="text-xl font-bold">{currencies.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Check className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Enabled Currencies</p>
                  <p className="text-xl font-bold">{enabledCurrencies}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="countries">
          <TabsList className="mb-4">
            <TabsTrigger value="countries" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Countries
            </TabsTrigger>
            <TabsTrigger value="content" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Content
            </TabsTrigger>
            <TabsTrigger value="locations" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Locations
            </TabsTrigger>
            <TabsTrigger value="footer" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Footer
            </TabsTrigger>
            <TabsTrigger value="currencies" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Currencies
            </TabsTrigger>
          </TabsList>

          {/* Countries Tab */}
          <TabsContent value="countries">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Countries</CardTitle>
                  <CardDescription>Enable or disable countries for your marketplace</CardDescription>
                </div>
                <Button onClick={() => setShowAddCountry(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Country
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Flag</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Default</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {countries.map((country, index) => (
                      <TableRow key={country.code} className={!country.enabled ? 'opacity-50' : ''}>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => moveCountry(country.code, 'up')}
                              disabled={index === 0}
                            >
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => moveCountry(country.code, 'down')}
                              disabled={index === countries.length - 1}
                            >
                              <ArrowDown className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <img 
                            src={country.flag} 
                            alt={country.name}
                            className="w-8 h-6 rounded object-cover"
                          />
                        </TableCell>
                        <TableCell className="font-mono font-bold">{country.code}</TableCell>
                        <TableCell>{country.name}</TableCell>
                        <TableCell>
                          <span className="font-mono">{country.currency_symbol} {country.currency}</span>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={country.enabled}
                            onCheckedChange={() => toggleCountry(country.code, country.enabled)}
                            data-testid={`toggle-country-${country.code}`}
                          />
                        </TableCell>
                        <TableCell>
                          {country.code === defaultCountry ? (
                            <Badge className="bg-amber-100 text-amber-800">
                              <Star className="h-3 w-3 mr-1 fill-amber-500" />
                              Default
                            </Badge>
                          ) : country.enabled ? (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setAsDefaultCountry(country.code)}
                            >
                              Set Default
                            </Button>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => deleteCountry(country.code)}
                            disabled={country.code === defaultCountry}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Tab - Country-specific content management */}
          <TabsContent value="content">
            <Card>
              <CardHeader>
                <CardTitle>Country Content</CardTitle>
                <CardDescription>Customize hero text and taglines for each country</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Country</TableHead>
                      <TableHead>Hero Title</TableHead>
                      <TableHead>Tagline</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {countries.map((country) => (
                      <TableRow key={country.code}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <img src={country.flag} alt={country.name} className="w-6 h-4 rounded object-cover" />
                            <span className="font-medium">{country.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <p className="truncate text-sm text-gray-600">
                            {country.content?.hero_title || `Buy & Sell Anything in ${country.name}`}
                          </p>
                        </TableCell>
                        <TableCell className="max-w-[150px]">
                          <p className="truncate text-sm text-gray-600">
                            {country.content?.tagline || `${country.name}'s trusted marketplace`}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge className={country.content?.enabled !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {country.content?.enabled !== false ? 'Custom' : 'Default'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditContent(country)}
                            data-testid={`edit-content-${country.code}`}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Locations Tab - Top Locations management */}
          <TabsContent value="locations">
            <Card>
              <CardHeader>
                <CardTitle>Top Locations (SEO)</CardTitle>
                <CardDescription>Manage location links shown above the footer for better SEO</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Country</TableHead>
                      <TableHead>Section Title</TableHead>
                      <TableHead>Locations Count</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {countries.map((country) => (
                      <TableRow key={country.code}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <img src={country.flag} alt={country.name} className="w-6 h-4 rounded object-cover" />
                            <span className="font-medium">{country.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {country.top_locations?.title || 'Top Locations'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {country.top_locations?.locations?.length || 0} locations
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={country.top_locations?.enabled !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {country.top_locations?.enabled !== false ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditLocations(country)}
                            data-testid={`edit-locations-${country.code}`}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Footer Tab - Full Footer Customization */}
          <TabsContent value="footer">
            <Card>
              <CardHeader>
                <CardTitle>Footer Configuration</CardTitle>
                <CardDescription>Customize footer content (contact info, links, categories) for each country</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Country</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Quick Links</TableHead>
                      <TableHead>Categories</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {countries.map((country) => (
                      <TableRow key={country.code}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <img src={country.flag} alt={country.name} className="w-6 h-4 rounded object-cover" />
                            <span className="font-medium">{country.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-gray-600 truncate max-w-[120px]">
                            {country.footer?.contact?.address || 'Not set'}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {country.footer?.quick_links?.links?.length || 0} links
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {country.footer?.categories?.links?.length || 0} links
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={country.footer?.enabled !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {country.footer?.enabled !== false ? 'Custom' : 'Default'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditFooter(country)}
                            data-testid={`edit-footer-${country.code}`}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Currencies Tab */}
          <TabsContent value="currencies">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Currencies</CardTitle>
                  <CardDescription>Enable or disable currencies for your marketplace</CardDescription>
                </div>
                <Button onClick={() => setShowAddCurrency(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Currency
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Default</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currencies.map((currency) => (
                      <TableRow key={currency.code} className={!currency.enabled ? 'opacity-50' : ''}>
                        <TableCell className="text-xl font-semibold">{currency.symbol}</TableCell>
                        <TableCell className="font-mono font-bold">{currency.code}</TableCell>
                        <TableCell>{currency.name}</TableCell>
                        <TableCell>
                          <Switch
                            checked={currency.enabled}
                            onCheckedChange={() => toggleCurrency(currency.code, currency.enabled)}
                            data-testid={`toggle-currency-${currency.code}`}
                          />
                        </TableCell>
                        <TableCell>
                          {currency.code === defaultCurrency ? (
                            <Badge className="bg-amber-100 text-amber-800">
                              <Star className="h-3 w-3 mr-1 fill-amber-500" />
                              Default
                            </Badge>
                          ) : currency.enabled ? (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setAsDefaultCurrency(currency.code)}
                            >
                              Set Default
                            </Button>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => deleteCurrency(currency.code)}
                            disabled={currency.code === defaultCurrency}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Country Dialog */}
        <Dialog open={showAddCountry} onOpenChange={setShowAddCountry}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Country</DialogTitle>
              <DialogDescription>Add a new country to your marketplace</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Country Code</Label>
                  <Input 
                    placeholder="e.g., US"
                    value={newCountry.code}
                    onChange={(e) => setNewCountry(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    maxLength={3}
                  />
                </div>
                <div>
                  <Label>Country Name</Label>
                  <Input 
                    placeholder="e.g., United States"
                    value={newCountry.name}
                    onChange={(e) => setNewCountry(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Flag URL</Label>
                <Input 
                  placeholder="https://flagcdn.com/w40/us.png"
                  value={newCountry.flag}
                  onChange={(e) => setNewCountry(prev => ({ ...prev, flag: e.target.value }))}
                />
                <p className="text-xs text-gray-500 mt-1">Use flagcdn.com/w40/[country-code].png</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Currency Code</Label>
                  <Input 
                    placeholder="e.g., USD"
                    value={newCountry.currency}
                    onChange={(e) => setNewCountry(prev => ({ ...prev, currency: e.target.value.toUpperCase() }))}
                  />
                </div>
                <div>
                  <Label>Currency Symbol</Label>
                  <Input 
                    placeholder="e.g., $"
                    value={newCountry.currency_symbol}
                    onChange={(e) => setNewCountry(prev => ({ ...prev, currency_symbol: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Distance Unit</Label>
                <Input 
                  placeholder="km or miles"
                  value={newCountry.distance_unit}
                  onChange={(e) => setNewCountry(prev => ({ ...prev, distance_unit: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddCountry(false)}>Cancel</Button>
              <Button onClick={addCountry} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Add Country
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Currency Dialog */}
        <Dialog open={showAddCurrency} onOpenChange={setShowAddCurrency}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Currency</DialogTitle>
              <DialogDescription>Add a new currency to your marketplace</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Currency Code</Label>
                  <Input 
                    placeholder="e.g., USD"
                    value={newCurrency.code}
                    onChange={(e) => setNewCurrency(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    maxLength={3}
                  />
                </div>
                <div>
                  <Label>Symbol</Label>
                  <Input 
                    placeholder="e.g., $"
                    value={newCurrency.symbol}
                    onChange={(e) => setNewCurrency(prev => ({ ...prev, symbol: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Currency Name</Label>
                <Input 
                  placeholder="e.g., US Dollar"
                  value={newCurrency.name}
                  onChange={(e) => setNewCurrency(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddCurrency(false)}>Cancel</Button>
              <Button onClick={addCurrency} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Add Currency
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Content Dialog */}
        <Dialog open={showEditContent} onOpenChange={setShowEditContent}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingCountry && (
                  <img src={editingCountry.flag} alt={editingCountry.name} className="w-6 h-4 rounded" />
                )}
                Edit Content for {editingCountry?.name}
              </DialogTitle>
              <DialogDescription>
                Customize the hero section and tagline text for this country
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Hero Title</Label>
                <Input 
                  placeholder="Buy & Sell Anything in..."
                  value={editingContent.hero_title}
                  onChange={(e) => setEditingContent(prev => ({ ...prev, hero_title: e.target.value }))}
                />
                <p className="text-xs text-gray-500 mt-1">Main headline shown on homepage</p>
              </div>
              <div>
                <Label>Hero Subtitle</Label>
                <Textarea 
                  placeholder="Join thousands of people..."
                  value={editingContent.hero_subtitle}
                  onChange={(e) => setEditingContent(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                  rows={2}
                />
                <p className="text-xs text-gray-500 mt-1">Secondary text below the headline</p>
              </div>
              <div>
                <Label>Tagline</Label>
                <Input 
                  placeholder="Your trusted marketplace"
                  value={editingContent.tagline}
                  onChange={(e) => setEditingContent(prev => ({ ...prev, tagline: e.target.value }))}
                />
                <p className="text-xs text-gray-500 mt-1">Short tagline shown in "Why Choose" section</p>
              </div>
              <div>
                <Label>Footer Description</Label>
                <Textarea 
                  placeholder="Country's largest marketplace..."
                  value={editingContent.footer_description}
                  onChange={(e) => setEditingContent(prev => ({ ...prev, footer_description: e.target.value }))}
                  rows={2}
                />
                <p className="text-xs text-gray-500 mt-1">Description text shown in website footer</p>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-sm">Enable Custom Content</Label>
                  <p className="text-xs text-gray-500">Turn off to use default content</p>
                </div>
                <Switch
                  checked={editingContent.enabled}
                  onCheckedChange={(checked) => setEditingContent(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditContent(false)}>Cancel</Button>
              <Button onClick={saveContent} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Content
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Locations Dialog */}
        <Dialog open={showEditLocations} onOpenChange={setShowEditLocations}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-600" />
                {editingCountry && (
                  <img src={editingCountry.flag} alt={editingCountry.name} className="w-6 h-4 rounded" />
                )}
                Top Locations for {editingCountry?.name}
              </DialogTitle>
              <DialogDescription>
                Add location links for SEO. These will appear above the footer.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Section Title</Label>
                <Input 
                  placeholder="Top Locations"
                  value={editingLocations.title}
                  onChange={(e) => setEditingLocations(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div>
                <Label>Locations (comma-separated)</Label>
                <Textarea 
                  placeholder="London, Manchester, Birmingham, Glasgow..."
                  value={editingLocations.locationsText}
                  onChange={(e) => setEditingLocations(prev => ({ ...prev, locationsText: e.target.value }))}
                  rows={4}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter city/area names separated by commas. These become clickable links for better SEO.
                </p>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-sm">Enable Top Locations</Label>
                  <p className="text-xs text-gray-500">Show/hide this section on the website</p>
                </div>
                <Switch
                  checked={editingLocations.enabled}
                  onCheckedChange={(checked) => setEditingLocations(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditLocations(false)}>Cancel</Button>
              <Button onClick={saveLocations} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Locations
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Footer Dialog */}
        <Dialog open={showEditFooter} onOpenChange={setShowEditFooter}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                {editingCountry && (
                  <img src={editingCountry.flag} alt={editingCountry.name} className="w-6 h-4 rounded" />
                )}
                Footer Configuration for {editingCountry?.name}
              </DialogTitle>
              <DialogDescription>
                Customize the footer content including contact info, quick links, and categories.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {/* Contact Section */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Contact Information</Label>
                  <Switch
                    checked={editingFooter.contact.enabled}
                    onCheckedChange={(checked) => setEditingFooter(prev => ({
                      ...prev,
                      contact: { ...prev.contact, enabled: checked }
                    }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Section Title</Label>
                    <Input 
                      placeholder="Contact Us"
                      value={editingFooter.contact.title}
                      onChange={(e) => setEditingFooter(prev => ({
                        ...prev,
                        contact: { ...prev.contact, title: e.target.value }
                      }))}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Phone</Label>
                    <div className="flex items-center gap-2">
                      <Input 
                        placeholder="+1 234 567 8900"
                        value={editingFooter.contact.phone}
                        onChange={(e) => setEditingFooter(prev => ({
                          ...prev,
                          contact: { ...prev.contact, phone: e.target.value }
                        }))}
                        className="flex-1"
                      />
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <span>Show</span>
                        <Switch
                          checked={editingFooter.contact.phone_enabled}
                          onCheckedChange={(checked) => setEditingFooter(prev => ({
                            ...prev,
                            contact: { ...prev.contact, phone_enabled: checked }
                          }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Address</Label>
                  <Input 
                    placeholder="123 Main Street, City, Country"
                    value={editingFooter.contact.address}
                    onChange={(e) => setEditingFooter(prev => ({
                      ...prev,
                      contact: { ...prev.contact, address: e.target.value }
                    }))}
                  />
                </div>
                <div>
                  <Label className="text-sm">Email</Label>
                  <Input 
                    placeholder="support@zeilian.com"
                    value={editingFooter.contact.email}
                    onChange={(e) => setEditingFooter(prev => ({
                      ...prev,
                      contact: { ...prev.contact, email: e.target.value }
                    }))}
                  />
                </div>
              </div>

              {/* Quick Links Section */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Quick Links</Label>
                  <Switch
                    checked={editingFooter.quick_links.enabled}
                    onCheckedChange={(checked) => setEditingFooter(prev => ({
                      ...prev,
                      quick_links: { ...prev.quick_links, enabled: checked }
                    }))}
                  />
                </div>
                <div>
                  <Label className="text-sm">Section Title</Label>
                  <Input 
                    placeholder="Quick Links"
                    value={editingFooter.quick_links.title}
                    onChange={(e) => setEditingFooter(prev => ({
                      ...prev,
                      quick_links: { ...prev.quick_links, title: e.target.value }
                    }))}
                  />
                </div>
                <div>
                  <Label className="text-sm">Links (one per line: Label|URL)</Label>
                  <Textarea 
                    placeholder="Home|/&#10;Browse Ads|/category/all&#10;Post Ad|/post-ad"
                    value={editingFooter.quickLinksText}
                    onChange={(e) => setEditingFooter(prev => ({ ...prev, quickLinksText: e.target.value }))}
                    rows={5}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format: Label|URL (one link per line). Example: Home|/
                  </p>
                </div>
              </div>

              {/* Categories Section */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Categories Links</Label>
                  <Switch
                    checked={editingFooter.categories.enabled}
                    onCheckedChange={(checked) => setEditingFooter(prev => ({
                      ...prev,
                      categories: { ...prev.categories, enabled: checked }
                    }))}
                  />
                </div>
                <div>
                  <Label className="text-sm">Section Title</Label>
                  <Input 
                    placeholder="Categories"
                    value={editingFooter.categories.title}
                    onChange={(e) => setEditingFooter(prev => ({
                      ...prev,
                      categories: { ...prev.categories, title: e.target.value }
                    }))}
                  />
                </div>
                <div>
                  <Label className="text-sm">Links (one per line: Label|URL)</Label>
                  <Textarea 
                    placeholder="Electronics|/category/electronics&#10;Vehicles|/category/vehicles"
                    value={editingFooter.categoriesText}
                    onChange={(e) => setEditingFooter(prev => ({ ...prev, categoriesText: e.target.value }))}
                    rows={5}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format: Label|URL (one link per line). Example: Electronics|/category/electronics
                  </p>
                </div>
              </div>

              {/* Disclaimer Section */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Disclaimer Text</Label>
                  <Switch
                    checked={editingFooter.disclaimer?.enabled}
                    onCheckedChange={(checked) => setEditingFooter(prev => ({
                      ...prev,
                      disclaimer: { ...prev.disclaimer, enabled: checked }
                    }))}
                  />
                </div>
                <div>
                  <Label className="text-sm">Disclaimer (shown below social icons)</Label>
                  <Textarea 
                    placeholder="While every care has been taken..."
                    value={editingFooter.disclaimer?.text || ''}
                    onChange={(e) => setEditingFooter(prev => ({
                      ...prev,
                      disclaimer: { ...prev.disclaimer, text: e.target.value }
                    }))}
                    rows={3}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Legal disclaimer text displayed below social media icons
                  </p>
                </div>
              </div>

              {/* Enable/Disable Custom Footer */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-sm">Enable Custom Footer</Label>
                  <p className="text-xs text-gray-500">Turn off to use default footer content</p>
                </div>
                <Switch
                  checked={editingFooter.enabled}
                  onCheckedChange={(checked) => setEditingFooter(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditFooter(false)}>Cancel</Button>
              <Button onClick={saveFooter} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Footer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminLocalization;
